/**
 * Reset the agent — protected on purpose:
 *   1. choose: back to the starter template, or EMPTY (rebuild from scratch with the builder);
 *   2. see what will be removed (the preview counts everything, items you added or changed included);
 *   3. type the agent's name AND the word RESET;
 * a full backup is kept for 30 days and "Undo" puts everything back exactly (the builder conversation too,
 * when it was cleared).
 *
 * API: GET /agent-builder/reset · POST /agent-builder/reset {mode, confirmName, confirm:'RESET', clearBuilder}
 *      POST /agent-builder/reset/backups/:id/undo
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useToast } from '../../Context/ToastContext';
import { Row, Help, Muted, Input, GhostBtn, SmallBtn, errText, fmtWhen } from './styles';

const Overlay = styled.div` position: fixed; inset: 0; background: rgba(0,0,0,0.65); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 16px; `;
const Dialog = styled.div` background: #161b22; border: 1px solid rgba(248,81,73,0.45); border-radius: 12px; padding: 20px 22px; width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto; color: #e6edf3; `;
const Choice = styled.label` display: flex; gap: 10px; padding: 10px 12px; border-radius: 10px; border: 1px solid ${p => (p.$on ? '#f85149' : 'rgba(255,255,255,0.12)')}; background: ${p => (p.$on ? 'rgba(248,81,73,0.08)' : 'transparent')}; cursor: pointer; margin-bottom: 8px; font-size: 13px; line-height: 1.45; `;
const DangerSolid = styled.button` background: #da3633; color: #fff; border: none; border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 600; cursor: pointer; &:hover { background: #f85149; } &:disabled { opacity: .45; cursor: default; } `;

export default function ResetDialog({ api, onClose, onDone }) {
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState('template');
  const [clearBuilder, setClearBuilder] = useState(false);
  const [name, setName] = useState('');
  const [word, setWord] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/agent-builder/reset'); setData(r.data); } catch (e) { showToast(errText(e, 'Could not load the reset preview'), 'error'); }
  }, [api, showToast]);
  useEffect(() => { load(); }, [load]);

  const p = data?.preview && !data.preview.error ? data.preview : null;
  const agentName = p?.agentName || '';
  const canTemplate = !!p?.canReset;
  const ready = !!p && (mode === 'empty' || canTemplate) && name.trim().toLowerCase() === agentName.trim().toLowerCase() && word === 'RESET';

  const run = async () => {
    setBusy(true);
    try {
      await api.post('/agent-builder/reset', { mode, confirmName: name, confirm: 'RESET', clearBuilder });
      showToast(mode === 'empty' ? 'Your agent is empty now — rebuild it with the builder. Undo is available for 30 days.' : 'Your agent is back to the starter template. Undo is available for 30 days.', 'success');
      onDone?.();
      onClose();
    } catch (e) { showToast(errText(e, 'The reset did not happen'), 'error'); } finally { setBusy(false); }
  };
  const undo = async (id) => {
    if (!window.confirm('Put the agent back exactly as it was before this reset? What it holds now is kept in a new backup.')) return;
    setBusy(true);
    try { await api.post(`/agent-builder/reset/backups/${id}/undo`); showToast('The agent is back as it was.', 'success'); onDone?.(); await load(); } catch (e) { showToast(errText(e, 'Undo failed'), 'error'); } finally { setBusy(false); }
  };

  return (
    <Overlay onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <Dialog>
        <h3 style={{ margin: '0 0 6px', fontSize: 17 }}>Reset your agent</h3>
        <Help style={{ marginBottom: 12 }}>This removes what your agent knows. A full backup is kept for 30 days and you can undo it below.</Help>
        {!data ? <Muted>Loading…</Muted> : (
          <>
            <Choice $on={mode === 'template'}>
              <input type="radio" checked={mode === 'template'} onChange={() => setMode('template')} disabled={!canTemplate} />
              <div>
                <b>Back to the starter template</b>{p?.template ? ` (${p.template.name}, version ${p.template.version})` : ''}
                <div style={{ color: '#8b98a5' }}>Your agent becomes what it was on day one. Everything you or the builder added or changed is removed.{!canTemplate ? ' — No starter template applies to your agent.' : ''}</div>
              </div>
            </Choice>
            <Choice $on={mode === 'empty'}>
              <input type="radio" checked={mode === 'empty'} onChange={() => setMode('empty')} />
              <div>
                <b>Start from empty</b>
                <div style={{ color: '#8b98a5' }}>All routes, topics, strategies, rules and corrections are removed. The agent keeps its instructions so it still answers, and you rebuild it with the builder.</div>
              </div>
            </Choice>

            {p && (
              <div style={{ background: '#0b0f14', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.6, margin: '6px 0 10px' }}>
                <b>What will be removed:</b> {p.totals.items} item{p.totals.items === 1 ? '' : 's'} in total — {p.totals.notFromTemplate} you added, {p.totals.editedTemplateItems} template item{p.totals.editedTemplateItems === 1 ? '' : 's'} you changed.
                <div style={{ color: '#8b98a5' }}>{p.collections.filter(c => c.total).map(c => `${c.label}: ${c.total}`).join(' · ')}</div>
              </div>
            )}

            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, margin: '6px 0 12px' }}>
              <input type="checkbox" checked={clearBuilder} onChange={e => setClearBuilder(e.target.checked)} style={{ marginTop: 3 }} />
              <span>Also clear our conversation and what the builder knows about my practice (kept in the backup too).</span>
            </label>

            <Muted>Type the agent’s name: <b style={{ color: '#e6edf3' }}>{agentName}</b></Muted>
            <Input value={name} onChange={e => setName(e.target.value)} style={{ margin: '4px 0 10px' }} />
            <Muted>Type <b style={{ color: '#e6edf3' }}>RESET</b> to confirm</Muted>
            <Input value={word} onChange={e => setWord(e.target.value)} style={{ margin: '4px 0 14px' }} />

            <Row style={{ justifyContent: 'flex-end' }}>
              <GhostBtn disabled={busy} onClick={onClose}>Cancel</GhostBtn>
              <DangerSolid disabled={!ready || busy} onClick={run}>{busy ? 'Working…' : mode === 'empty' ? 'Empty my agent' : 'Reset to the template'}</DangerSolid>
            </Row>

            {(data.backups || []).length > 0 && (
              <div style={{ marginTop: 18, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Earlier resets</div>
                {data.backups.map(b => (
                  <Row key={b.id} style={{ justifyContent: 'space-between', padding: '5px 0', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <Muted>{fmtWhen(b.createdAt)} · {b.mode === 'empty' ? 'emptied' : b.reason === 'undo' ? 'before an undo' : 'to template'} · {b.items} items{b.clearedBuilder ? ' · conversation cleared' : ''}</Muted>
                    {b.canUndo ? <SmallBtn disabled={busy} onClick={() => undo(b.id)}>Undo</SmallBtn> : <Muted>{b.status}</Muted>}
                  </Row>
                ))}
              </div>
            )}
          </>
        )}
      </Dialog>
    </Overlay>
  );
}
